import { useState, useEffect } from "react";
import { X, Quote, TrendingUp, Star } from "lucide-react";

const DIRECTOR_MESSAGES = [
  {
    title: "Speed Wins Deals",
    message: "Call every enquiry within 5 minutes — customers book with the FIRST company that makes them feel valued. Say their name, listen to their worries, and remind them: 'We don't charge for double trips, key delays, or working past 5pm. Your move is done when it's done.'",
    tip: "Magic words: \"I'll personally make sure your move day is stress-free.\"",
  },
  {
    title: "Sell Peace of Mind, Not a Van",
    message: "Customers aren't buying a lorry — they're buying a smooth start to their new life. Paint the picture: 'Imagine handing us the keys and relaxing while our DBS-checked crew handles everything.' Then ask for the deposit while the feeling is fresh.",
    tip: "Magic words: \"Shall I secure your date now? Popular dates go fast.\"",
  },
  {
    title: "Follow Up Like a Pro",
    message: "80% of bookings happen after the 2nd or 3rd contact. Check your 'Chase Today' list every morning. A friendly call — 'Just checking you got our quote, any questions at all?' — converts silent quotes into paid deposits.",
    tip: "Magic words: \"Most customers ask me about X — shall I explain?\"",
  },
  {
    title: "The Deposit Close",
    message: "Never end a call without asking for commitment. A £100 deposit locks the customer in and stops them shopping around. Make it easy: 'A small deposit secures your exact date and crew — takes 2 minutes by bank transfer.'",
    tip: "Magic words: \"Once your deposit is in, that date is 100% yours.\"",
  },
  {
    title: "Objections Are Buying Signals",
    message: "When a customer says 'it's expensive' — they're interested! Respond with value, not discounts: 'We include dismantling, reassembly, and we never charge extra for delays. Cheaper quotes often add fees on the day.'",
    tip: "Magic words: \"What matters most to you — the lowest price, or zero surprises on move day?\"",
  },
];

export const DirectorMessage = ({ branchName }) => {
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState(DIRECTOR_MESSAGES[0]);

  useEffect(() => {
    if (sessionStorage.getItem("hg_show_director") === "1") {
      setMsg(DIRECTOR_MESSAGES[Math.floor(Math.random() * DIRECTOR_MESSAGES.length)]);
      setVisible(true);
      sessionStorage.removeItem("hg_show_director");
    }
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="director-message-overlay">
      <div className="w-full max-w-lg bg-gradient-to-b from-zinc-950 to-black border-2 border-[#D4AF37] rounded-2xl shadow-[0_0_60px_rgba(212,175,55,0.35)] overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#D4AF37] via-[#F4D03F] to-[#D4AF37] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-black" />
            <h2 className="text-sm font-bold text-black uppercase tracking-widest">Message From The Director</h2>
          </div>
          <button onClick={() => setVisible(false)} className="text-black/60 hover:text-black" data-testid="director-message-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <Quote className="w-8 h-8 text-[#D4AF37] shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-white mb-1">{msg.title}</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{msg.message}</p>
            </div>
          </div>

          <div className="bg-zinc-900 border border-[#D4AF37]/40 rounded-xl p-4 mb-5">
            <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Today's Magic Words
            </p>
            <p className="text-sm text-white italic">{msg.tip}</p>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-zinc-500">
              Good luck today, <span className="text-[#D4AF37] font-semibold">{branchName || "Team"}</span> — turn every lead into a booking! 🏆
            </p>
            <button
              onClick={() => setVisible(false)}
              className="px-5 py-2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-black text-sm font-bold rounded-full hover:from-[#c9a532] hover:to-[#a67908] transition-all"
              data-testid="director-message-lets-go"
            >
              Let's Go! 💪
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
